import {BusinessOutlined, ExpandMore} from '@mui/icons-material';
import {alpha, Box, ListItemText, ListSubheader, Menu, MenuItem, SvgIcon, Typography} from '@mui/material';
import {FC, useMemo, useState} from 'react';
import {FormattedMessage} from 'react-intl';
import {
    bexioAccountsApi,
    businessesApi,
    chargersApi,
    companyApi,
    contractsApi,
    customInvoiceItemsApi,
    employeeApi,
    invoicesApi,
    locationsApi,
    notificationApi,
    pricingApi,
    reOwnerApi,
    reportServiceApi,
    reportsApi,
    tagsApi,
    tariffsApi,
    transactionsApi,
    usersApi,
} from 'src/api';
import {useAppDispatch, useAppSelector} from 'src/app/hooks';
import {useCan} from 'src/permissions/hooks';
import {setActiveBusiness} from 'src/stores/common/commonSlice';
import theme from 'src/theme';

/**
 * Which business the platform administrator is working in.
 *
 * Everyone else belongs to exactly one and never sees this: their business comes from their account,
 * and `useBusinessId` prefers it. The administrator belongs to none, which used to mean every screen
 * that asked "my business" read null - so the choice had to live somewhere, and the sidebar is where
 * it applies to everything below it rather than to one form at a time.
 *
 * Gated on being able to list every business rather than on the account type: that permission is what
 * makes the list fetchable in the first place, so anyone who cannot read it has nothing to choose from.
 */
/**
 * Everything whose contents belong to one business.
 *
 * Switching business does not narrow the data on screen - it replaces it, so every cached answer from
 * the previous one is wrong rather than stale. Most pages fetch on mount, so without this the
 * locations, chargers and invoices of the business just left stay on screen until something happens to
 * remount them, which is worse than a spinner: it is the right layout with another tenant's numbers.
 *
 * businessesApi is deliberately absent: the list being chosen from is the same list either way.
 */
export const TENANT_SCOPED_APIS = [
    bexioAccountsApi,
    chargersApi,
    companyApi,
    contractsApi,
    customInvoiceItemsApi,
    employeeApi,
    invoicesApi,
    locationsApi,
    notificationApi,
    pricingApi,
    reOwnerApi,
    reportServiceApi,
    reportsApi,
    tagsApi,
    tariffsApi,
    transactionsApi,
    usersApi,
];

export const ActiveBusinessSelect: FC = () => {
    const canListBusinesses = useCan('businesses.readPlatform');
    const dispatch = useAppDispatch();
    const {authInfo, activeBusiness} = useAppSelector((state) => state.common);
    const ownBusiness = authInfo?.business?.id;
    const [anchor, setAnchor] = useState<null | HTMLElement>(null);

    const {data} = businessesApi.useGetBusinessesQuery(
        {params: {page: 0, size: 200}},
        {skip: !canListBusinesses || !!ownBusiness}
    );
    const businesses = useMemo(() => data?.content ?? [], [data]);
    const current = businesses.find((business) => business.id === activeBusiness);

    if (!canListBusinesses || ownBusiness) {
        return null;
    }

    const choose = (id: number) => {
        setAnchor(null);
        if (id === activeBusiness) {
            return;
        }
        dispatch(setActiveBusiness(id));
        TENANT_SCOPED_APIS.forEach((api) => dispatch(api.util.resetApiState()));
    };

    return (
        <Box sx={{width: '100%', mt: 0.5, mb: 1}}>
            <Box
                onClick={(event) => setAnchor(event.currentTarget)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: theme.spacing(10 / 8),
                    // The padding and type of a navigation link, so it stands exactly as tall as the
                    // assistant button above it. It was a white MUI Select before, which read as a
                    // form dropped into the sidebar and stood a different height from everything else.
                    // One pixel less than the assistant button's padding, because this one
                    // carries a border and that button does not: the two then stand exactly
                    // the same height.
                    padding: theme.spacing(11 / 8, 13 / 8),
                    borderRadius: theme.spacing(8 / 8),
                    cursor: 'pointer',
                    background: alpha('#fff', 0.06),
                    border: `1px solid ${alpha('#fff', 0.12)}`,
                    transition: 'background 0.2s ease, border-color 0.2s ease',
                    '&:hover': {
                        background: alpha('#fff', 0.1),
                        borderColor: alpha('#fff', 0.2),
                    },
                }}
            >
                <SvgIcon
                    component={BusinessOutlined}
                    sx={{fontSize: 20, color: theme.palette.neutral[400], flexShrink: 0}}
                />
                {/* The name alone, on one line. What it means is said once, at the head of the menu
                    it opens, rather than as a caption that made the control taller than every other
                    row in the column. */}
                <Typography
                    variant="subtitle1"
                    noWrap
                    sx={{flex: 1, minWidth: 0, color: theme.palette.common.white}}
                >
                    {current?.name ?? '\u00A0'}
                </Typography>
                <ExpandMore
                    sx={{
                        fontSize: 20,
                        color: theme.palette.neutral[400],
                        flexShrink: 0,
                        transform: anchor ? 'rotate(180deg)' : 'none',
                        transition: 'transform 0.2s ease',
                    }}
                />
            </Box>

            <Menu
                anchorEl={anchor}
                open={Boolean(anchor)}
                onClose={() => setAnchor(null)}
                anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
                transformOrigin={{vertical: 'top', horizontal: 'left'}}
                PaperProps={{
                    sx: {
                        mt: 0.5,
                        // A ceiling rather than a height: on a short viewport 360px is taller than
                        // the screen, and the menu spills past the bottom of it.
                        maxHeight: 'min(360px, 50dvh)',
                        // Exactly the control it opens from, not a menu that grows to its widest
                        // name and hangs over the page beside a button half its width. A name that
                        // does not fit scrolls sideways within it.
                        width: anchor?.offsetWidth,
                        overflowX: 'auto',
                        borderRadius: '10px',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
                    },
                }}
            >
                <ListSubheader
                    sx={{
                        fontSize: '0.6875rem',
                        fontWeight: 700,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        color: theme.palette.neutral[400],
                        lineHeight: '32px',
                    }}
                >
                    <FormattedMessage id="activeBusiness"/>
                </ListSubheader>
                {businesses.map((business) => (
                    <MenuItem
                        key={business.id}
                        selected={business.id === activeBusiness}
                        onClick={() => choose(business.id)}
                    >
                        <ListItemText
                            primaryTypographyProps={{
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                whiteSpace: 'nowrap',
                            }}
                            primary={business.name}
                        />
                    </MenuItem>
                ))}
            </Menu>
        </Box>
    );
};
